import type { ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import { Bot, FileText, ShoppingCart, UserRound } from 'lucide-react'
import { cn, formatMoney } from '@/lib/utils'
import { StaffAvatar } from '@/components/staff-avatar'
import { SenderAvatar } from '@/features/communications/message-row'
import { formatMessageTime } from '@/features/communications/message-format'
import { formatFileSize } from '@/features/communications/formatting'
import type {
  ChatAttachment,
  ChatCartAction,
  ChatMessage,
  ChatProductCard,
} from '@/features/storefront-chat/types'
import { StoreLink } from '@/features/storefront-chat/pages'
import { attachmentUrl, readLabel, storeUrl } from '@/features/storefront-chat/format'

const VIA_LABEL: Record<ChatMessage['via'], string> = {
  widget: 'chat',
  dashboard: 'dashboard',
  slack: 'Slack',
  email: 'email',
}

const MARKDOWN =
  'prose prose-sm dark:prose-invert max-w-none break-words text-sm leading-6 prose-p:my-0 prose-p:leading-6 prose-ul:my-0 prose-ol:my-0 prose-li:my-0.5 prose-a:text-usa-blue prose-a:underline-offset-2 prose-strong:text-foreground [&>*+*]:mt-2'

// Each line break staff type stays a line break, as in the shopper's widget (USA-1552).
const REMARK_PLUGINS = [remarkBreaks]

function ProductCards({ cards }: { cards: ChatProductCard[] }) {
  if (cards.length === 0) return null
  return (
    <ul className="grid gap-2 pt-1 @xl/message:grid-cols-2">
      {cards.map((card) => (
        <li key={`${card.handle}-${card.variant_id ?? ''}`}>
          <StoreLink
            href={storeUrl(card.url)}
            className="flex items-center gap-3 rounded-md border bg-background p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {card.image_url ? (
              <img
                src={card.image_url}
                alt=""
                loading="lazy"
                className="h-12 w-12 shrink-0 rounded object-contain bg-white"
              />
            ) : (
              <span aria-hidden className="h-12 w-12 shrink-0 rounded bg-muted" />
            )}
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 text-xs font-medium leading-snug text-foreground">
                {card.title}
              </span>
              <span className="mt-0.5 flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
                {card.price && <span className="font-medium text-foreground">{formatMoney(card.price)}</span>}
                {card.available === false && <span>Unavailable</span>}
              </span>
            </span>
          </StoreLink>
        </li>
      ))}
    </ul>
  )
}

/** Photos as thumbnails and other files by name; each opens in a new tab (USA-1323). */
function Attachments({ attachments, shopper }: { attachments: ChatAttachment[]; shopper: boolean }) {
  if (attachments.length === 0) return null
  const photos = attachments.filter((attachment) => attachment.kind === 'image')
  const files = attachments.filter((attachment) => attachment.kind !== 'image')
  return (
    <div className={cn('flex flex-col gap-1.5', shopper ? 'items-start' : 'items-end')}>
      {photos.length > 0 && (
        <ul aria-label="Photos" className={cn('flex flex-wrap gap-2', !shopper && 'justify-end')}>
          {photos.map((photo) => (
            <li key={photo.id}>
              <StoreLink
                href={attachmentUrl(photo)}
                className="block overflow-hidden rounded-lg border bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <img
                  src={attachmentUrl(photo)}
                  alt={photo.filename}
                  loading="lazy"
                  className="h-28 w-28 object-cover sm:h-32 sm:w-32"
                />
              </StoreLink>
            </li>
          ))}
        </ul>
      )}
      {files.length > 0 && (
        <ul aria-label="Files" className="w-full max-w-sm space-y-1">
          {files.map((file) => (
            <li key={file.id}>
              <StoreLink
                href={attachmentUrl(file)}
                className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{file.filename}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{formatFileSize(file.size_bytes)}</span>
              </StoreLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function cartChange(action: ChatCartAction) {
  if (action.action === 'add') return `Added ${action.quantity}`
  if (action.action === 'remove') return 'Removed'
  return `Changed ${action.previous_quantity ?? '?'} → ${action.quantity}`
}

/** The changes the AI made in the shopper's cart with this answer. */
function CartActions({ actions }: { actions: ChatCartAction[] }) {
  if (actions.length === 0) return null
  return (
    <ul aria-label="Cart changes" className="max-w-full space-y-1 rounded-md border bg-muted/30 px-3 py-2 text-xs">
      {actions.map((action) => (
        <li key={`${action.action}-${action.line_key ?? action.variant_id}`} className="flex min-w-0 items-start gap-2 leading-snug">
          <ShoppingCart aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="shrink-0 font-medium tabular-nums text-foreground">{cartChange(action)}</span>
          {action.handle ? (
            <StoreLink
              href={storeUrl(`/products/${action.handle}`)}
              className="line-clamp-2 min-w-0 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {action.title}
            </StoreLink>
          ) : (
            <span className="line-clamp-2 min-w-0 text-muted-foreground">{action.title}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/** One chat message as a bubble: the shopper on the left, our replies (AI and staff) on the right,
 * the time underneath. Who wrote it is on the avatar's hover. */
export function ChatMessageRow({
  message,
  shopperName,
  streaming = false,
  readAt,
  editor,
  actions,
}: {
  message: Pick<ChatMessage, 'author' | 'body' | 'created_at'> &
    Partial<
      Pick<
        ChatMessage,
        'id' | 'staff_name' | 'staff_photo_url' | 'via' | 'product_cards' | 'page' | 'cart_actions' | 'attachments' | 'edited_at'
      >
    >
  shopperName: string
  /** The AI is still writing this answer. */
  streaming?: boolean
  /** When the shopper read this reply: set on the newest reply they have seen (USA-1463). */
  readAt?: string | null
  /** Staff are editing this reply: the box that replaces its text (USA-1548). */
  editor?: ReactNode
  /** Edit and delete, beside the time. */
  actions?: ReactNode
}) {
  if (message.author === 'system') {
    return (
      <div className="flex justify-center px-4 py-2">
        <p className="rounded-full bg-muted px-3 py-1 text-center text-xs text-muted-foreground">
          {message.body} ·{' '}
          <time dateTime={message.created_at} className="tabular-nums">
            {formatMessageTime(message.created_at)}
          </time>
        </p>
      </div>
    )
  }
  const shopper = message.author === 'shopper'
  const name = shopper ? shopperName : message.author === 'ai' ? 'AI' : message.staff_name || 'Staff'
  const sender =
    message.author === 'staff' && message.via && message.via !== 'widget'
      ? `${name} · from ${VIA_LABEL[message.via]}`
      : name
  return (
    <article
      data-testid={message.id ? `chat-message-${message.id}` : undefined}
      aria-label={`${name}${streaming ? ', writing' : ''}`}
      className={cn(
        'group/message @container/message flex items-start gap-2 px-3 py-1.5 sm:px-4 @max-[36rem]/chat:gap-1.5 @max-[36rem]/chat:px-2 @max-[36rem]/chat:py-1',
        !shopper && 'flex-row-reverse',
      )}
    >
      {/* A phone-width chat keeps the avatars small, leaving the bubbles room. */}
      <span title={sender} className="@max-[36rem]/chat:[&>*]:size-6 @max-[36rem]/chat:[&>*]:text-[10px]">
        {shopper ? (
          <SenderAvatar tone="counterpart" name={shopperName} />
        ) : message.author === 'staff' && message.staff_photo_url ? (
          <StaffAvatar name={name} photoUrl={message.staff_photo_url} />
        ) : (
          <SenderAvatar tone="ours" icon={message.author === 'ai' ? Bot : UserRound} />
        )}
      </span>
      <div
        className={cn(
          'flex min-w-0 max-w-[85%] flex-col gap-1.5 @xl/message:max-w-[75%] @max-[36rem]/chat:gap-1',
          shopper ? 'items-start' : 'items-end',
          editor && 'w-full',
        )}
      >
        {editor}
        {!editor && (message.body || streaming) && (
        <div
          className={cn(
            'min-w-0 max-w-full rounded-2xl px-3.5 py-2 @max-[36rem]/chat:px-3 @max-[36rem]/chat:py-1.5',
            shopper ? 'rounded-bl-md bg-muted text-foreground' : 'rounded-br-md bg-usa-blue/10 text-foreground dark:bg-usa-blue/25',
          )}
        >
          {shopper ? (
            <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
          ) : (
            <div className={cn(MARKDOWN, '@max-[36rem]/chat:[&_li]:leading-5.5 @max-[36rem]/chat:[&_p]:leading-5.5')}>
              <Markdown
                remarkPlugins={REMARK_PLUGINS}
                components={{
                  a: ({ href, children }) => <StoreLink href={href ? storeUrl(href) : '#'}>{children}</StoreLink>,
                }}
              >
                {message.body}
              </Markdown>
            </div>
          )}
        </div>
        )}
        <Attachments attachments={message.attachments ?? []} shopper={shopper} />
        <CartActions actions={message.cart_actions ?? []} />
        <ProductCards cards={message.product_cards ?? []} />
        <span className="px-1 text-[11px] tabular-nums text-muted-foreground">
          {streaming ? (
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-usa-blue motion-reduce:animate-none" />
              Writing…
            </span>
          ) : (
            <>
              <time dateTime={message.created_at}>{formatMessageTime(message.created_at)}</time>
              {message.edited_at && (
                <span title={formatMessageTime(message.edited_at)}>
                  {' · '}Edited
                </span>
              )}
              {readAt && (
                <>
                  {' · '}
                  <time dateTime={readAt} data-testid="chat-read-receipt">
                    {readLabel(readAt, message.created_at)}
                  </time>
                </>
              )}
              {actions}
            </>
          )}
        </span>
      </div>
    </article>
  )
}
